import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { compose } from 'recompose';
import pageWithProps from 'shared/hoc/pageWithProps';
import { getDocumentByType } from 'shared/utils/documents';
import { H1, Error, FlexColumn, TamplateField, SubmitButton, StyledLink } from 'shared/components';
import { useForm, FormContext } from 'react-hook-form';
import { Loading } from 'shared/bo-components';
import { apiPrefix } from 'shared/utils/api';

const statusOptions = {
  SUCCESS: 'success',
  INVALID: 'inValid',
  ERROR: 'serverError',
};

const PageWrapper = styled(FlexColumn)`
  justify-content: center;
  align-items: center;
  height: 600px;
`;

const PageTitle = styled(H1)`
  text-align: center;
  margin-top: 15px;
`;

const FormWrapper = styled.form`
  display: flex;
  width: 700px;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;
`;

const Status = styled(Error)`
  height: 20px;
  margin-bottom: 15px;
  color: ${({ status, theme }) => (status === statusOptions.SUCCESS ? theme.palette.cyan : 'red')};
`;

const Link = styled(StyledLink)`
  color: ${({ theme }) => theme.palette.cyan};
  margin-top: 20px;
`;

const LoadingWrapper = styled.div`
  margin-top: 20px;
  height: 20px;
`;

const ResetPassword = () => {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState();

  const methods = useForm({
    mode: 'onChange',
  });

  const email = methods.watch('email');

  useEffect(() => {
    setStatus(null);
  }, [email]);

  const handleSubmit = useCallback(async ({ email: emailValue }) => {
    setStatus(null);

    const options = {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: emailValue }),
    };
    setIsLoading(true);
    const res = await fetch(`${apiPrefix}/users/recover-password`, options);
    setIsLoading(false);

    if (res.status === 200) {
      return setStatus(statusOptions.SUCCESS);
    }
    if (res.status === 401) {
      return setStatus(statusOptions.INVALID);
    }
    return setStatus(statusOptions.ERROR);
  }, []);

  const handleFieldFocus = useCallback(() => {
    setStatus(null);
  }, []);

  return (
    <PageWrapper>
      <PageTitle>Password Recovery</PageTitle>
      <FormContext {...methods}>
        <FormWrapper onSubmit={methods.handleSubmit(handleSubmit)}>
          <TamplateField
            title="Email"
            component="EmailField"
            name="email"
            placeholder="Email"
            showTitle={false}
            onFocus={handleFieldFocus}
            noBottomMargin
            required
          />
          <Status status={status}>
            {status &&
              (status === statusOptions.SUCCESS
                ? 'Recovery email sent to your email address'
                : status === statusOptions.INVALID
                ? 'Invalid Email'
                : 'Server Error')}
          </Status>
          <SubmitButton disabled={!methods.formState.isValid} text="Send" />
          <Link route="login">Back to Login</Link>
          <LoadingWrapper>{isLoading && <Loading text="Sending recovery email…" />}</LoadingWrapper>
        </FormWrapper>
      </FormContext>
    </PageWrapper>
  );
};

const enhance = compose(
  pageWithProps(async ({ lang, ref }) => ({
    ...(await getDocumentByType('reset-password', lang, ref)),
  })),
);

export default enhance(ResetPassword);
